import React, { useEffect, useMemo, useState } from "react";
import { Mail, Inbox, Star, Send, AlertCircle, Sparkles, Search } from "lucide-react";
import { toast } from "sonner";
import api, { streamSSE } from "@/lib/api";

const CATEGORIES = [
    { key: "primary", label: "Primary", dot: "bg-[#16a34a]" },
    { key: "updates", label: "Updates", dot: "bg-blue-500" },
    { key: "promotions", label: "Promotions", dot: "bg-purple-500" },
    { key: "social", label: "Social", dot: "bg-pink-500" },
    { key: "spam", label: "Spam", dot: "bg-gray-400" },
];

const PRIORITY_STYLE = {
    urgent: "bg-red-50 text-red-700 border-red-200",
    high: "bg-amber-50 text-amber-800 border-amber-200",
    normal: "bg-gray-100 text-gray-700 border-gray-200",
    low: "bg-gray-50 text-gray-500 border-gray-200",
};

export default function Email() {
    const [threads, setThreads] = useState([]);
    const [filters, setFilters] = useState({ category: null, starred: false, search: "" });
    const [selected, setSelected] = useState(null);
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [replyText, setReplyText] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [composing, setComposing] = useState(false);
    const [composeTo, setComposeTo] = useState("");
    const [composeSubject, setComposeSubject] = useState("");
    const [composeBody, setComposeBody] = useState("");

    const load = async () => {
        const params = {};
        if (filters.category) params.category = filters.category;
        if (filters.starred) params.starred = true;
        if (filters.search) params.search = filters.search;
        const { data } = await api.get("/email/threads", { params });
        setThreads(data.threads || []);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.category, filters.starred, filters.search]);

    const openThread = async (t) => {
        setSelected(t.id);
        setReplyText("");
        const { data } = await api.get(`/email/threads/${t.id}`);
        setThread(data.thread);
        setMessages(data.messages || []);
    };

    const suggestReply = async () => {
        if (!messages.length) return;
        const last = messages[messages.length - 1];
        setStreaming(true);
        setReplyText("");
        try {
            await streamSSE(`/email/messages/${last.id}/ai-reply`, {}, {
                onDelta: (_, all) => setReplyText(all),
                onDone: (full) => setReplyText(full),
            });
        } catch (e) {
            toast.error(String(e.message || e));
        } finally {
            setStreaming(false);
        }
    };

    const sendReply = async () => {
        if (!replyText.trim() || !thread) return;
        const to = thread.participants.filter((p) => !p.includes("@workforce.ai")).slice(0, 3);
        try {
            await api.post("/email/compose", {
                to,
                subject: thread.subject.startsWith("Re: ") ? thread.subject : `Re: ${thread.subject}`,
                body: replyText,
                thread_id: thread.id,
            });
            toast.success("Reply sent");
            setReplyText("");
            await openThread(thread);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    const sendCompose = async () => {
        if (!composeTo.trim() || !composeSubject.trim()) return toast.error("Add To and Subject");
        try {
            await api.post("/email/compose", {
                to: composeTo.split(",").map((s) => s.trim()).filter(Boolean),
                subject: composeSubject,
                body: composeBody,
            });
            toast.success("Sent");
            setComposing(false); setComposeTo(""); setComposeSubject(""); setComposeBody("");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Send failed");
        }
    };

    const toggleStar = async (msgId) => {
        try { await api.post(`/email/messages/${msgId}/star`); if (thread) await openThread(thread); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const unreadCount = useMemo(() => threads.filter((t) => !t.is_read).length, [threads]);

    return (
        <div className="h-full flex bg-white">
            {/* LEFT rail */}
            <aside className="w-[220px] flex-shrink-0 border-r border-[#e8e8e3] bg-[#fafaf7] p-4 overflow-y-auto wfos-scrollbar">
                <button
                    onClick={() => setComposing(true)}
                    data-testid="email-compose"
                    className="w-full inline-flex items-center justify-center gap-1 rounded-lg bg-[#16a34a] px-3 h-9 text-xs font-medium text-white hover:bg-[#15803d] mb-4"
                >
                    <Mail className="w-3.5 h-3.5" strokeWidth={2} /> Compose
                </button>

                <ul className="space-y-0.5 mb-4">
                    <li>
                        <button
                            onClick={() => setFilters({ category: null, starred: false, search: "" })}
                            data-testid="email-folder-inbox"
                            className={`w-full text-left rounded-md px-2 h-8 flex items-center justify-between text-sm hover:bg-[#f4f4ef] ${!filters.category && !filters.starred ? "bg-white border border-[#e8e8e3]" : ""}`}
                        >
                            <span className="inline-flex items-center gap-2"><Inbox className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.75} /> Inbox</span>
                            {unreadCount > 0 && <span className="text-[10px] text-gray-500">{unreadCount}</span>}
                        </button>
                    </li>
                    <li>
                        <button
                            onClick={() => setFilters({ category: null, starred: true, search: "" })}
                            data-testid="email-folder-starred"
                            className={`w-full text-left rounded-md px-2 h-8 flex items-center gap-2 text-sm hover:bg-[#f4f4ef] ${filters.starred ? "bg-white border border-[#e8e8e3]" : ""}`}
                        >
                            <Star className="w-3.5 h-3.5 text-gray-500" strokeWidth={1.75} /> Starred
                        </button>
                    </li>
                </ul>

                <div className="wfos-label px-2 mb-1">Categories</div>
                <ul className="space-y-0.5">
                    {CATEGORIES.map((c) => (
                        <li key={c.key}>
                            <button
                                onClick={() => setFilters({ ...filters, category: c.key, starred: false })}
                                data-testid={`email-category-${c.key}`}
                                className={`w-full text-left rounded-md px-2 h-8 flex items-center gap-2 text-sm hover:bg-[#f4f4ef] ${filters.category === c.key ? "bg-white border border-[#e8e8e3]" : ""}`}
                            >
                                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} /> {c.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>

            {/* THREADS list */}
            <section className="w-[380px] flex-shrink-0 border-r border-[#e8e8e3] flex flex-col">
                <div className="p-3 border-b border-[#e8e8e3]">
                    <div className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                        <input
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            data-testid="email-search"
                            placeholder="Search messages…"
                            className="w-full h-8 rounded-md border border-[#e8e8e3] pl-7 pr-2 text-xs"
                        />
                    </div>
                </div>
                <ul className="flex-1 overflow-y-auto wfos-scrollbar divide-y divide-[#e8e8e3]">
                    {threads.map((t) => (
                        <li key={t.id}>
                            <button
                                onClick={() => openThread(t)}
                                data-testid={`email-thread-${t.id}`}
                                className={`w-full text-left px-4 py-2.5 hover:bg-[#fafaf7] ${selected === t.id ? "bg-[#f0fdf4]" : ""}`}
                            >
                                <div className="flex items-center justify-between gap-2 mb-0.5">
                                    <div className={`truncate text-[13px] ${t.is_read ? "text-gray-700" : "font-semibold text-gray-900"}`}>{t.latest_from || "(unknown)"}</div>
                                    <div className="text-[10px] text-gray-400 flex-shrink-0">{new Date(t.last_activity_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                                </div>
                                <div className={`truncate text-[13px] ${t.is_read ? "text-gray-700" : "font-medium text-gray-900"}`}>{t.subject}</div>
                                <div className="mt-0.5 flex items-center gap-2 min-w-0">
                                    <span className={`wfos-pill text-[9px] border ${PRIORITY_STYLE[t.ai_priority || "normal"]}`}>{t.ai_priority || "normal"}</span>
                                    <span className="text-[11px] text-gray-500 truncate">{t.latest_snippet}</span>
                                </div>
                            </button>
                        </li>
                    ))}
                    {threads.length === 0 && (
                        <li className="text-xs text-gray-400 p-6 text-center">No messages match your filters.</li>
                    )}
                </ul>
            </section>

            {/* THREAD view */}
            <main className="flex-1 flex flex-col min-w-0 bg-white">
                {thread ? (
                    <>
                        <div className="px-6 py-4 border-b border-[#e8e8e3]">
                            <div className="text-lg font-medium text-gray-900">{thread.subject}</div>
                            <div className="mt-1 text-xs text-gray-500">
                                {thread.participants.slice(0, 4).join(" · ")} · {thread.message_count} message{thread.message_count === 1 ? "" : "s"}
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto wfos-scrollbar px-6 py-4 space-y-4" data-testid="email-thread-messages">
                            {messages.map((m) => (
                                <div key={m.id} className="wfos-card p-4">
                                    <div className="flex items-center justify-between text-xs mb-2">
                                        <div className="min-w-0">
                                            <div className="font-medium text-gray-900 truncate">{m.from_name} <span className="text-gray-400 font-normal">&lt;{m.from_email}&gt;</span></div>
                                            <div className="text-gray-400">{new Date(m.received_at || m.created_at).toLocaleString()}</div>
                                        </div>
                                        <button onClick={() => toggleStar(m.id)} className="text-gray-400 hover:text-amber-500">
                                            <Star className={`w-4 h-4 ${m.is_starred ? "fill-amber-500 text-amber-500" : ""}`} strokeWidth={1.75} />
                                        </button>
                                    </div>
                                    <div className="text-[13px] leading-relaxed text-gray-800 whitespace-pre-wrap">{m.body_text}</div>
                                </div>
                            ))}
                        </div>

                        {/* Reply composer */}
                        <div className="border-t border-[#e8e8e3] p-4">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="wfos-label">Reply</div>
                                <button
                                    onClick={suggestReply}
                                    disabled={streaming}
                                    data-testid="email-suggest-reply"
                                    className="inline-flex items-center gap-1 rounded-md border border-[#16a34a] bg-[#dcfce7] px-2 h-7 text-xs font-medium text-[#16a34a] hover:bg-[#bbf7d0] disabled:opacity-50"
                                >
                                    <Sparkles className="w-3 h-3" strokeWidth={2} /> {streaming ? "Drafting…" : "Suggest reply"}
                                </button>
                            </div>
                            <textarea
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                data-testid="email-reply-textarea"
                                placeholder="Type or use Suggest reply…"
                                className="w-full h-32 rounded-md border border-[#e8e8e3] bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#16a34a]"
                            />
                            <div className="mt-2 flex justify-end">
                                <button
                                    onClick={sendReply}
                                    disabled={!replyText.trim() || streaming}
                                    data-testid="email-send-reply"
                                    className="inline-flex items-center gap-1 rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white hover:bg-[#15803d] disabled:opacity-50"
                                >
                                    <Send className="w-3.5 h-3.5" strokeWidth={2} /> Send
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 grid place-items-center text-sm text-gray-500 gap-2">
                        <AlertCircle className="w-6 h-6 text-gray-300" strokeWidth={1.5} />
                        Select a thread on the left to open it.
                    </div>
                )}
            </main>

            {composing && (
                <div className="fixed inset-0 bg-black/30 grid place-items-center z-50">
                    <div className="wfos-card p-6 w-[560px]" data-testid="email-compose-modal">
                        <div className="text-lg font-medium text-gray-900">New message</div>
                        <div className="mt-4 space-y-3">
                            <input value={composeTo} onChange={(e) => setComposeTo(e.target.value)} placeholder="To: email@example.com" className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm" />
                            <input value={composeSubject} onChange={(e) => setComposeSubject(e.target.value)} placeholder="Subject" className="w-full h-9 rounded-md border border-[#e8e8e3] px-2 text-sm" />
                            <textarea value={composeBody} onChange={(e) => setComposeBody(e.target.value)} placeholder="Message…" className="w-full h-40 rounded-md border border-[#e8e8e3] px-2 py-1.5 text-sm resize-none" />
                        </div>
                        <div className="mt-6 flex justify-end gap-2">
                            <button onClick={() => setComposing(false)} className="rounded-md border border-[#e8e8e3] px-3 h-8 text-xs text-gray-700">Cancel</button>
                            <button onClick={sendCompose} className="rounded-md bg-[#16a34a] px-3 h-8 text-xs font-medium text-white">Send</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
